import { Component, OnInit, Injector, ViewChild } from '@angular/core';
import { SessionBaseComponent } from 'src/app/shared/components/session.base.component';
import { PermissionConstants } from 'src/app/core/constants/permission-constants.constant';
import { UserPreferenceConstants } from 'src/app/core/constants/user-preference-constants.constant';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { DataTableComponent } from 'src/app/shared/components/data-table/data-table.component';
import { deepCopy, isNullOrEmpty } from 'src/app/core/utils/common-functions';
import { PageInfo } from 'src/app/core/models/page-info';
import { finalize } from 'rxjs/operators';
import { PoServiceService } from 'src/app/features/transport-operation/services/po-service.service';
import { DeliveryOrderService } from 'src/app/features/transport-operation/services/delivery-order.service';
import { GlobalConstants } from 'src/app/core/constants/global-constants.constant';
import { LocalStorageService } from 'src/app/core/services/local-storage.service';
import { AsnInfoSearchRequest } from 'src/app/core/models/transport-operation/asn-info-search-request';
import { DropdownOptionsService } from '../../../../core/services/dropdown-options.service';

@Component({
  selector: 'laas-asn-info-search',
  templateUrl: './asn-info-search.component.html',
  styleUrls: ['./asn-info-search.component.scss']
})
export class AsnInfoSearchComponent extends SessionBaseComponent implements OnInit {

  readonly preferenceKey: string;
  readonly permissionCode = PermissionConstants.ViewAsnInfoSearch;
  readonly defaultSearchParams: any;
  currentLang: string = this.laasSetting.language;

  @ViewChild('headerTable') dataTable!: DataTableComponent;

  isSearchFormOpen = true;
  isHeaderTableOpen = true;
  searchFormGroup: FormGroup;
  searchParams: AsnInfoSearchRequest = new AsnInfoSearchRequest();

  totalResult = '';

  pageInfo: PageInfo = {
    pageSize: 5,
    pageIndex: 0,
    totalRecords: 0,
    totalPages: 0,
   };

  facilityList: any[] = [];
  storerMappingOptions: any[] = [];
  doStatusOptions: any[] = [];
  brandOptions: any[] = [];
  specLocTypeOptions: any[] = [];
  columns: any[] = [];
  searchResults: any[] = [];
  isLoading = false;

  constructor(
    injector: Injector,
    private formBuilder: FormBuilder,
    private poServiceService: PoServiceService,
    private doService: DeliveryOrderService,
    private localStorageService: LocalStorageService,
    private dropdownOptionService: DropdownOptionsService,
  ) {
    super(injector);
    this.preferenceKey = UserPreferenceConstants.StockInfoSearch;

    this.searchFormGroup = this.formBuilder.group({
      mo: null,
      sku: null,
      plant: [null, Validators.required],
      facility: [null, Validators.required],
      storer: [null, Validators.required]
    });

    this.columns = [
      { field: 'mo', header: 'AsnInfo.HeaderTable.MO', sort: true },
      { field: 'externMoveOrderKey', header: 'AsnInfo.HeaderTable.ExternMoveOrderKey', sort: true },
      { field: 'orderDocStatus', header: 'AsnInfo.HeaderTable.OrderDocStatus', sort: true },
      { field: 'eta', header: 'AsnInfo.HeaderTable.ETA', dataType: 'date', sort: true },
      { field: 'etd', header: 'AsnInfo.HeaderTable.ETD', dataType: 'date', sort: true },
      { field: 'sku', header: 'AsnInfo.HeaderTable.SKU', sort: true },
      { field: 'specLocType', header: 'AsnInfo.HeaderTable.SpecLocType', sort: true },
      { field: 'openQty', header: 'AsnInfo.HeaderTable.ExpectedQty', sort: true },
      { field: 'shippedQty', header: 'AsnInfo.HeaderTable.ShippedQty', sort: true },
    ];

  }

  async ngOnInit(): Promise<void> {
    await super.ngOnInit();
    this.logger.info('StockInfoSearchComponent onInit.');

    this.poServiceService.getMappingFacilityOptions().subscribe({
      next: (result) => {
        const seenCodes = new Set<string>();
        this.facilityList = result
          .filter((x: { optionDesc: string }) => {
            if (seenCodes.has(x.optionDesc)) return false;
            seenCodes.add(x.optionDesc);
            return true;
          })
          .map((x: { optionDesc: string; optionCode: string }) => ({
            label: x.optionDesc,
            value: x.optionDesc,
          }));
        this.searchFormGroup.get('facility')?.setValue(this.facilityList[0].value);
      }
    });


    this.doService.getSpecLocType().subscribe({
      next: (result) => {
        const seenSpecLocTypes = new Set<string>();
        this.specLocTypeOptions = result.specLocTypeList
          .filter((x: { storer: any; facility: any; specLocType: string }) => {
            if (x.storer !== 'WISTRON_BBP' || x.facility !== 'LK') return false;
            if (seenSpecLocTypes.has(x.specLocType)) return false;
            seenSpecLocTypes.add(x.specLocType);
            return true;
          })
          .map((x: { specLocType: any; }) => ({
            label: x.specLocType,
            value: x.specLocType
          }));
      }
    });

    this.dropdownOptionService.getDOStatusOptions()
      .subscribe({
        next: (options: any[]) => {
          this.doStatusOptions = options.map(x => ({ label: x.optionDesc, value: x.optionCode }));
        }
    });

    this.storerMappingOptions = ['WISTRON_BBP'];
    this.searchFormGroup.get('storer')?.setValue(this.storerMappingOptions[0]);
  }

  chipsAdd(event: any, fromControlName: any): void {
    const value: string = event.value;
    const parsedValues: string[] = value.split('\r\n').filter(x => isNullOrEmpty(x) === false && x !== '\r\n');
    const formControl = this.searchFormGroup.get(fromControlName);
    formControl?.value.splice(formControl?.value.indexOf(value));
    formControl?.setValue(formControl?.value.concat(parsedValues));
  }

  searchBtnClick(): void {
    this.searchAsnInfo();
  }

  searchAsnInfo(): void {
    if (this.searchFormGroup.invalid) {
      this.translateService.get('Common.Toast.CheckRequiredFields')
        .subscribe({
          next: result => {
            this.toastService.warn(result);
          }
      });
      return;
    }
    this.dataTable.loading = true;
    this.searchParams = deepCopy(this.searchFormGroup.value);

    this.doService.searchAsnInfo(this.searchParams)
    .pipe(finalize(() => this.dataTable.loading = false))
    .subscribe({
      next: (resp: any) => {
        const cacheStr = localStorage.getItem('laas_cache');
        if (cacheStr) {
          try {
            const cacheObj = JSON.parse(cacheStr);
            const rawData = cacheObj?.dropdownList?.value?.DoStatus || [];
            if (rawData.length > 0) {
              this.doStatusOptions = rawData.map((item: any) => ({
              label: `${item.codeKey} - ${item.codeDesc}`,
              value: item.codeKey
              }));
            }
          } catch (e) { }
        }

        if (resp.statusCountMap && Object.keys(resp.statusCountMap).length > 0) {
          this.totalResult = Object.entries(resp.statusCountMap).map(([code, count]) => {
            const option = this.doStatusOptions.find(opt => String(opt.value) === String(code));
            const label = option ? option.label : code;
            return `${label}: ${count}`;
          }).join('，');
          } else {
            this.totalResult = '';
            }
        this.searchResults = resp.data.orderTrackingData;
        this.dataTable.updateTableData(this.searchResults);
      },
      error: (err) => {
        (isNullOrEmpty(err.error.errorMsg)) ?
        this.toastService.error(this.translateService.instant('Common.Message.UnknowError')) :
        this.toastService.error(this.translateService.instant(err.error.errorMsg));

        this.searchResults = [];
      },
      complete: () => {
        this.isSearchFormOpen = false;
      }
    });
  }

  resetBtnClick(): void {
    this.searchFormGroup.reset();
  }

  get language(): string {
    return this.localStorageService.getItem(GlobalConstants.languageKey);
  }

  gotoProgress(event: Event, doNo: string): void {
    this.routeStateService.navigateTo(
      '/TransportOperation/ShipmentProgress',
      { doNo: doNo }, true);
  }
}
